import Style from 'ol/style/Style';
import { TimelineLayerFadingTransition } from 'cadenza/api-client/timeline-dto';

import { MINIMAL_FADING_FACTOR } from '../timeline-constants';

export class CachingProxyStyle extends Style {

  constructor (style, fadingSupplier) {
    super();
    this._style = style;
    this._fadingSupplier = fadingSupplier;
    this.hidden = false;
  }

  getFill () {
    return this.hidden ? null : this._style.getFill();
  }

  getStroke () {
    return this.hidden ? null : this._style.getStroke();
  }

  getText () {
    return this.hidden ? null : this._style.getText();
  }

  getImage () {
    return this.hidden ? null : this._style.getImage();
  }

  getZIndex () {
    return this._style.getZIndex();
  }

  /**
   * Returns the cached updated style, or if none present, creates one and updates the cache
   *
   * @protected
   * @param cache - the cache to search for styles
   * @param baseImage - the style to update
   * @return the updated style
   */
  getUpdated (cache, baseImage) {
    const { fadingFactor, fadingTransition } = this._fadingSupplier();

    const roundedFadingFactor = roundToOneDecimalPlaces(fadingFactor);

    const key = this.getKey(roundedFadingFactor, fadingTransition);
    const cached = cache.get(key);

    if (cached !== undefined) {
      return cached;
    }

    const newObject = this.createImage(roundedFadingFactor, fadingTransition, baseImage);
    cache.set(key, newObject);
    return newObject;
  }

  /**
   * Gets the key for this style
   *
   * @protected
   * @param fadingFactor - used to create the key
   * @param fadingTransition - used to create the key
   */
  getKey (fadingFactor, fadingTransition) {
    throw Error('Not implemented by child class');
  }

  /**
   * Creates an image using the fading props
   *
   * @protected
   * @param fadingFactor - for the image
   * @param fadingTransition - for the image
   * @param baseImage - to apply the fading props to
   */
  createImage (fadingFactor, fadingTransition, baseImage) {
    throw Error('Not implemented by child class');
  }

  /**
   * Calculates opacity based on fading factor and transition
   *
   * @protected
   * @param baseOpacity - to calculate from
   * @param fadingFactor - for the calculation
   * @param transition - the type of transition
   * @return the opacity
   */
  getOpacityForFadingFactor (baseOpacity, fadingFactor, transition) {
    return isDecreasingOpacityTransition(transition)
      ? scaleByFadingFactor(baseOpacity, fadingFactor)
      : baseOpacity;
  }

  /**
   * Calculates scale based on fading factor and transition
   *
   * @protected
   * @param baseScale - to calculate from
   * @param fadingFactor - for the calculation
   * @param transition - the type of transition
   * @return the scale
   */
  getScaleForFadingFactor (baseScale, fadingFactor, transition) {
    return isDecreasingSizeTransition(transition)
      ? scaleByFadingFactor(baseScale, fadingFactor)
      : baseScale;
  }

}

function isDecreasingSizeTransition (transition) {
  return TimelineLayerFadingTransition.TRANSPARENCY_AND_SIZE === transition
    || TimelineLayerFadingTransition.SIZE === transition;
}

function isDecreasingOpacityTransition (transition) {
  return TimelineLayerFadingTransition.TRANSPARENCY_AND_SIZE === transition
    || TimelineLayerFadingTransition.TRANSPARENCY === transition;
}

function scaleByFadingFactor (baseValue, fadingFactor) {
  // Scale between [baseValue * MINIMAL_FADING_FACTOR] and [baseValue]
  const scaled = (baseValue * MINIMAL_FADING_FACTOR)
    + (baseValue * (1 - MINIMAL_FADING_FACTOR) * fadingFactor);

  // We round to reduce the number of styles which also simplifies style key
  return roundToOneDecimalPlaces(scaled);
}

export function roundToOneDecimalPlaces (number) {
  /* We always round up to prevent values in range ]0, 0.05[ to get rounded down to 0.
  * Otherwise points with a fading value of ]0, 0.05[ (usually the last point) is not visible. */
  return Math.ceil(number * 10) / 10;
}
